import {
  GLOBAL_VARIABLE_TAG_KEY,
  TAG_TYPE_KEY,
  TREE_GLOBAL_VARIABLE_TAG_KEY,
} from '@/common/define/widget.define';
import type { TagRequest } from '@/api-client/data/model';
import type { TargetKeyNameItem } from '@/api-client/data/model/target-key-name-item';
import type {
  FilterVariablePreset,
  GlobalVariable,
  SelectedGlobalVariable,
  SelectedTreeVariable,
} from '@/dashboard/features/globalVariables/domain/globalVariable.types';
import type { TreeVarFilterDataForView } from '@/dashboard/features/globalVariables/components/globalVarFilter/types/globalVarFilter.type';
import { resolveObjectType } from '@/dashboard/features/globalVariables/domain/treeVariable.utils';
import type { ArgParam } from '@/dashboard/features/widgetsAPI/domain/referenceParam';
import type {
  MonitoringDashboardTarget,
  MonitoringDashboardTargetList,
  SelectedLegends,
  TargetChild,
} from './dashboardLegend.types';
import type { GridLayoutItem, WidgetModeType } from '@/dashboard/shared/types/widgets.types';

/**
 * targetList의 children을 필터링하고, children이 비어있으면 해당 target을 삭제합니다.
 */
export const filterTargetListChildren = (
  targetList: MonitoringDashboardTargetList,
  filterFn: (child: TargetChild) => boolean,
): void => {
  targetList.forEach((target, targetId) => {
    target.children = target.children.filter(filterFn);
    if (target.children.length === 0) {
      targetList.delete(targetId);
    }
  });
};

/**
 * 위젯에서 표시할 이름을 추출합니다.
 */
export const getWidgetDisplayName = (widget: GridLayoutItem | undefined): string => {
  if (!widget) {
    return '';
  }

  if (typeof widget.titleOption === 'string') {
    try {
      return JSON.parse(widget.titleOption || '{}')?.titleText ?? '';
    } catch {
      return '';
    }
  }

  return widget.titleOption?.titleText || widget.chartType || '';
};

/**
 * 타겟을 Map에 추가하거나 기존 타겟의 children을 업데이트합니다.
 */
export const addOrUpdateTargetToMap = (
  targetList: MonitoringDashboardTargetList,
  targetId: string,
  newTarget: MonitoringDashboardTarget,
): void => {
  if (!targetId || !newTarget.targetName) {
    return;
  }

  const existingTarget = targetList.get(targetId);

  if (existingTarget) {
    const newChild = newTarget.children[0];
    if (newChild) {
      const hasWidget = existingTarget.children.some(
        ([existingWidgetUUID]) => existingWidgetUUID === newChild[0],
      );
      if (!hasWidget) {
        existingTarget.children.push(newChild);
      }
    }
  } else {
    targetList.set(targetId, newTarget);
  }
};

type DashboardTargetsColumnLike = {
  targets: TargetKeyNameItem[];
  globalFilterIds?: number[];
  treeVariableIds?: number[];
};

export const shouldApplyPresetLegendTargetFilter = ({
  mode,
  isDashboardLegendOpen,
  selectedLegends,
}: {
  mode?: WidgetModeType;
  isDashboardLegendOpen: boolean;
  selectedLegends: SelectedLegends[] | null | undefined;
}): boolean => {
  return (
    mode === 'view' &&
    isDashboardLegendOpen &&
    (selectedLegends === null || Boolean(selectedLegends?.length))
  );
};

/**
 * 부모 depth만 선택된 트리 변수에서 해당 부모에 속하는 리프 타겟 ID를 추출한다.
 * filterChildDepthOptions(tree-variables.ts)의 parentTargetsById 매칭 로직과 동일.
 */
const resolveLeafTargetIdsFromParentSelections = (
  treeFilter: SelectedTreeVariable,
  treeVariable: TreeVarFilterDataForView,
): string[] => {
  const leafDepth = treeVariable.depths[treeVariable.depths.length - 1];
  if (!leafDepth) return [];

  const parentSelections: Record<string, Set<string>> = {};
  for (const { tagKey, tagValueId } of treeFilter.selectedList) {
    const resolvedKey = resolveObjectType(treeVariable.platform, tagKey);
    const existing = parentSelections[resolvedKey];
    if (existing) {
      existing.add(tagValueId);
    } else {
      parentSelections[resolvedKey] = new Set([tagValueId]);
    }
  }

  if (!Object.keys(parentSelections).length) return [];

  const leafTargetIds: string[] = [];
  for (const option of leafDepth.allOptions) {
    const parts = option.value.split('--');
    const tagId = parts.length > 1 ? parts[parts.length - 1] : option.value;
    const parentTargets = leafDepth.parentTargetsById[tagId];

    if (!parentTargets?.length) continue;

    const matches = Object.entries(parentSelections).every(([depthResolvedKey, selectedIds]) =>
      parentTargets.some((entry) => {
        const entryValue = entry[depthResolvedKey];
        return entryValue !== undefined && selectedIds.has(entryValue);
      }),
    );

    if (matches) {
      leafTargetIds.push(tagId);
    }
  }

  return leafTargetIds;
};

const getLeafSelectedTreeItems = (
  treeFilter: SelectedTreeVariable,
  treeVariable?: TreeVarFilterDataForView,
): SelectedTreeVariable['selectedList'] => {
  if (!treeVariable) {
    return treeFilter.selectedList.filter(({ tagType }) => tagType === TAG_TYPE_KEY.TAG);
  }

  const leafDepth = treeVariable.depths.at(-1);
  if (!leafDepth) {
    return [];
  }

  return treeFilter.selectedList.filter(({ tagKey, tagType }) => {
    return (
      tagType === TAG_TYPE_KEY.TAG ||
      tagKey === leafDepth.depthKey ||
      tagKey === leafDepth.resolvedTagKey
    );
  });
};

export const filterDashboardTargetsColumnsByPreset = <T extends DashboardTargetsColumnLike>({
  targetsColumns,
  presetList,
  currentPresetId,
  globalVariables,
  treeVariables = [],
}: {
  targetsColumns: T[];
  presetList: FilterVariablePreset[];
  currentPresetId: number;
  globalVariables: GlobalVariable[];
  treeVariables?: TreeVarFilterDataForView[];
}): T[] => {
  const currentPreset = presetList.find(({ userFilterId }) => userFilterId === currentPresetId);

  if (!currentPreset?.filters?.length && !currentPreset?.treeFilters?.length) {
    return targetsColumns;
  }

  const selectedTargetIdsByGlobalFilter = (currentPreset.filters ?? []).reduce(
    (filterMap, filter) => {
      if (filter.use === false || !filter.selectedList?.length) {
        return filterMap;
      }

      const matchedGlobalVariable = globalVariables.find(
        ({ globalFilterId }) => globalFilterId === filter.globalFilterId,
      );

      if (!matchedGlobalVariable || matchedGlobalVariable.variableType !== 'target') {
        return filterMap;
      }

      filterMap.set(
        filter.globalFilterId,
        new Set(filter.selectedList.map(({ tagId }) => `${tagId}`)),
      );

      return filterMap;
    },
    new Map<number, Set<string>>(),
  );

  const selectedTargetIdsByTreeVariable = (currentPreset.treeFilters ?? []).reduce(
    (treeMap, treeFilter) => {
      if (treeFilter.use === false || !treeFilter.selectedList?.length) {
        return treeMap;
      }

      const matchedTreeVar = treeVariables.find(({ id }) => id === treeFilter.treeVariableId);
      const leafTargetIds = getLeafSelectedTreeItems(treeFilter, matchedTreeVar).map(
        ({ tagValueId }) => `${tagValueId}`,
      );

      if (leafTargetIds.length) {
        treeMap.set(treeFilter.treeVariableId, new Set(leafTargetIds));
        return treeMap;
      }

      // 부모 depth만 선택된 경우: parentTargetsById로 리프 타겟 ID 해소
      if (matchedTreeVar) {
        const resolvedIds = resolveLeafTargetIdsFromParentSelections(treeFilter, matchedTreeVar);
        if (resolvedIds.length) {
          treeMap.set(treeFilter.treeVariableId, new Set(resolvedIds));
        }
      }

      return treeMap;
    },
    new Map<number, Set<string>>(),
  );

  if (!selectedTargetIdsByGlobalFilter.size && !selectedTargetIdsByTreeVariable.size) {
    return targetsColumns;
  }

  return targetsColumns.map((targetsColumn) => {
    const filteredTargetIds = [
      ...(targetsColumn.globalFilterIds?.flatMap((globalFilterId) =>
        Array.from(selectedTargetIdsByGlobalFilter.get(globalFilterId) ?? []),
      ) ?? []),
      ...(targetsColumn.treeVariableIds?.flatMap((treeVariableId) =>
        Array.from(selectedTargetIdsByTreeVariable.get(treeVariableId) ?? []),
      ) ?? []),
    ];

    if (!filteredTargetIds?.length) {
      return targetsColumn;
    }

    const filteredTargetIdSet = new Set(filteredTargetIds);

    return {
      ...targetsColumn,
      targets: targetsColumn.targets.filter(
        ({ id }) => id != null && filteredTargetIdSet.has(`${id}`),
      ),
    };
  });
};

const mergeRequestTargets = (targets: TagRequest[]): TagRequest[] => {
  return Array.from(
    targets
      .reduce<Map<string, TagRequest>>((targetMap, target) => {
        const tagKey = target.tagKey ?? '';
        const tagType = target.tagType ?? TAG_TYPE_KEY.TAG;
        const mapKey = `${tagKey}-${tagType}`;
        const tagValues = Array.from(new Set((target.tagValue ?? []).filter(Boolean)));

        if (!tagValues.length) {
          return targetMap;
        }

        const foundTarget = targetMap.get(mapKey);

        if (foundTarget) {
          foundTarget.tagValue = Array.from(
            new Set([...(foundTarget.tagValue ?? []), ...tagValues]),
          );
        } else {
          targetMap.set(mapKey, {
            tagKey,
            tagType: tagType as TagRequest['tagType'],
            tagValue: tagValues,
          });
        }

        return targetMap;
      }, new Map())
      .values(),
  );
};

const decodeArgParamTargets = (value: string): TagRequest[] => {
  if (!value) {
    return [];
  }

  return value.split(',').flatMap((argParam) => {
    const [tagKey, tagType, tagValue] = argParam.split(';');

    if (!tagKey || !tagType || !tagValue) {
      return [];
    }

    return [
      {
        tagKey,
        tagType: tagType as TagRequest['tagType'],
        tagValue: [tagValue],
      },
    ];
  });
};

const encodeRequestTargetsToArgParamValue = (targets: TagRequest[]): string => {
  return targets
    .flatMap(({ tagKey, tagType, tagValue }) =>
      (tagValue ?? []).map((value) => `${tagKey ?? ''};${tagType ?? TAG_TYPE_KEY.TAG};${value}`),
    )
    .join(',');
};

const expandGlobalVariableTarget = (
  target: TagRequest,
  filters: SelectedGlobalVariable[],
): TagRequest[] => {
  const nextTargets: TagRequest[] = [];
  const unresolvedGlobalFilterIds: string[] = [];

  (target.tagValue ?? []).forEach((globalFilterId) => {
    const presetFilter = filters.find(
      ({ globalFilterId: filterId }) => `${filterId}` === `${globalFilterId}`,
    );

    if (!presetFilter || presetFilter.use === false) {
      return;
    }

    if (!presetFilter.selectedList?.length) {
      unresolvedGlobalFilterIds.push(`${globalFilterId}`);
      return;
    }

    presetFilter.selectedList.forEach(({ groupTagId, tagType, tagId }) => {
      nextTargets.push({
        tagKey: groupTagId ?? '',
        tagType: tagType as TagRequest['tagType'],
        tagValue: [tagId],
      });
    });
  });

  if (unresolvedGlobalFilterIds.length) {
    nextTargets.push({
      ...target,
      tagKey: target.tagKey ?? GLOBAL_VARIABLE_TAG_KEY,
      tagType: TAG_TYPE_KEY.GLOBAL_VARIABLE as TagRequest['tagType'],
      tagValue: unresolvedGlobalFilterIds,
    });
  }

  return nextTargets;
};

const expandTreeGlobalVariableTarget = (
  target: TagRequest,
  treeFilters: SelectedTreeVariable[],
  treeVariables: TreeVarFilterDataForView[] = [],
): TagRequest[] => {
  const nextTargets: TagRequest[] = [];
  const unresolvedTreeVariableIds: string[] = [];

  (target.tagValue ?? []).forEach((treeVariableId) => {
    const treeFilter = treeFilters.find(
      ({ treeVariableId: filterId }) => `${filterId}` === `${treeVariableId}`,
    );
    const matchedTreeVar = treeVariables.find(({ id }) => `${id}` === `${treeVariableId}`);

    if (!treeFilter || treeFilter.use === false) {
      return;
    }

    if (!treeFilter.selectedList?.length) {
      unresolvedTreeVariableIds.push(`${treeVariableId}`);
      return;
    }

    const leafItems = getLeafSelectedTreeItems(treeFilter, matchedTreeVar);

    if (leafItems.length) {
      leafItems.forEach(({ tagKey, tagType, tagValueId }) => {
        nextTargets.push({
          tagKey: tagKey ?? '',
          tagType: tagType as TagRequest['tagType'],
          tagValue: [tagValueId],
        });
      });
      return;
    }

    // 부모 depth만 선택된 경우: parentTargetsById로 리프 타겟 해소
    if (matchedTreeVar) {
      const resolvedIds = resolveLeafTargetIdsFromParentSelections(treeFilter, matchedTreeVar);
      if (resolvedIds.length) {
        const leafDepth = matchedTreeVar.depths[matchedTreeVar.depths.length - 1];
        resolvedIds.forEach((tagValueId) => {
          nextTargets.push({
            tagKey: leafDepth?.resolvedTagKey ?? '',
            tagType: TAG_TYPE_KEY.TAG as TagRequest['tagType'],
            tagValue: [tagValueId],
          });
        });
        return;
      }
    }

    unresolvedTreeVariableIds.push(`${treeVariableId}`);
  });

  if (unresolvedTreeVariableIds.length) {
    nextTargets.push({
      ...target,
      tagKey: target.tagKey ?? TREE_GLOBAL_VARIABLE_TAG_KEY,
      tagType: TAG_TYPE_KEY.TREE_GLOBAL_VARIABLE as TagRequest['tagType'],
      tagValue: unresolvedTreeVariableIds,
    });
  }

  return nextTargets;
};

export const applyPresetSelectionToRequestTargets = ({
  targets,
  presetList,
  currentPresetId,
  treeVariables = [],
}: {
  targets: TagRequest[];
  presetList: FilterVariablePreset[];
  currentPresetId: number;
  treeVariables?: TreeVarFilterDataForView[];
}): TagRequest[] => {
  const currentPreset = presetList.find(({ userFilterId }) => userFilterId === currentPresetId);

  if (!currentPreset?.filters?.length && !currentPreset?.treeFilters?.length) {
    return targets;
  }

  const expandedTargets = targets.flatMap((target) => {
    if (target.tagType === TAG_TYPE_KEY.GLOBAL_VARIABLE) {
      if (!currentPreset.filters?.length) {
        return [target];
      }
      return expandGlobalVariableTarget(target, currentPreset.filters);
    }

    if (target.tagType === TAG_TYPE_KEY.TREE_GLOBAL_VARIABLE) {
      if (!currentPreset.treeFilters?.length) {
        return [target];
      }
      return expandTreeGlobalVariableTarget(target, currentPreset.treeFilters, treeVariables);
    }

    return [target];
  });

  return mergeRequestTargets(expandedTargets);
};

export const transformArgParamsWithPresetSelection = ({
  argParam,
  presetList,
  currentPresetId,
  treeVariables = [],
}: {
  argParam: ArgParam[] | undefined;
  presetList: FilterVariablePreset[];
  currentPresetId: number;
  treeVariables?: TreeVarFilterDataForView[];
}): ArgParam[] | undefined => {
  if (!argParam?.length) {
    return argParam;
  }

  return argParam.map((arg) => {
    const transformedArg: ArgParam = {};

    Object.entries(arg).forEach(([key, value]) => {
      if (!value?.tags || !value.value) {
        transformedArg[key] = value;
        return;
      }

      const decodedTargets = decodeArgParamTargets(value.value);
      const transformedTargets = applyPresetSelectionToRequestTargets({
        targets: decodedTargets,
        presetList,
        currentPresetId,
        treeVariables,
      });

      transformedArg[key] = {
        ...value,
        value: encodeRequestTargetsToArgParamValue(transformedTargets),
      };
    });

    return transformedArg;
  });
};
